// What a theme of the user's own puts on every slide (S6): a logo in a corner, footer words and the slide number, left
// off the slides the theme says (title slides, by default, as PowerPoint's "Don't show on title slide"). Drawn by the
// slide's face, so the editor, the strip, presenting, the shared viewer, the phone and the PDF all show the same.

import type { CSSProperties, ReactNode } from "react";
import { picturePath } from "../../../../shared/imageInfo";
import { chromeSkips, chromeSpots, dateWords, type Corner, type DeckChrome } from "../../../../shared/deckTheme";
import { cx } from "../../../lib/util";
import "../../../styles/slides-theme.css";
import type { SlideBg } from "../../../../shared/slideDesign";
import { spotClasses } from "../place/ChromeSpot";
import { useSpotReads } from "../place/chromeRead";

type Spot = Corner | "bc";

const SPOT_STYLE: Record<Spot, CSSProperties> = {
  tl: { left: 112, top: 40 },
  tr: { right: 112, top: 40, flexDirection: "row-reverse" },
  bl: { left: 112, bottom: 40 },
  br: { right: 112, bottom: 40, flexDirection: "row-reverse" },
  bc: { left: 0, right: 0, bottom: 40, justifyContent: "center" },
};

export function DeckChrome({ chrome, layout, number, over, ground }: { chrome: DeckChrome | null | undefined; layout: string; number?: number; over?: boolean; ground?: SlideBg | null }) {
  // SA: on a background picture, each corner reads the patch behind it: light or dark ink, a soft backing where busy
  const reads = useSpotReads(ground);
  if (!chrome || chromeSkips(chrome, layout)) return null;
  const spots = chromeSpots(chrome);
  if (!spots.size) return null;
  const out: ReactNode[] = [];
  for (const [spot, items] of spots) {
    // a corner's items side by side, the corner's own nearest it
    const kids = items.map((what) => {
      if (what === "logo" && chrome.logo) return <img key="logo" className="sl-chrome-logo" src={picturePath(chrome.logo.image.key)} alt="" draggable={false} decoding="async" style={{ height: chrome.logo.size }} />;
      if (what === "footer") return <span key="footer" className="sl-chrome-footer">{chrome.footer}</span>;
      if (what === "date") return <span key="date" className="sl-chrome-footer sl-chrome-date">{dateWords(chrome.date)}</span>;
      if (what === "number" && number) return <span key="number" className="sl-chrome-no">{number}</span>;
      return null;
    });
    out.push(
      <div key={spot} className={cx("sl-chrome", `is-${spot}`, over && !reads && "is-over", ...spotClasses(reads?.[spot]))} style={SPOT_STYLE[spot]} aria-hidden>
        {kids}
      </div>,
    );
  }
  return <>{out}</>;
}
